/**
 * Video Logger
 *
 * Specialized logger for video playback events and errors.
 * Provides video-specific context tracking.
 *
 * Delegates to appLogger so the `App.` prefix and any cross-cutting
 * appLogger behavior apply uniformly. Sources surface in the DB as
 * `App.VideoPlayer.<area>` after this consolidation.
 *
 * Usage:
 * ```typescript
 * import { videoLogger } from '@/domain/services/analytics/videoLogger';
 *
 * videoLogger.initStart(videoId, mediaTitle);
 * videoLogger.hlsError(videoId, errorType, errorDetails, fatal);
 * videoLogger.decryptionEvent(videoId, success, segmentUrl, error);
 * ```
 */

import { appLogger } from './appLogger';

class VideoLogger {
  private readonly SOURCE_PREFIX = 'VideoPlayer';

  /**
   * Log video initialization start
   */
  public initStart(videoId: string, mediaTitle: string): void {
    appLogger.info(
      'Video initialization started',
      `${this.SOURCE_PREFIX}.init`, { context: {
        videoId,
        mediaTitle,
        timestamp: Date.now(),
      } }
    );
  }

  /**
   * Log video initialization success
   */
  public initSuccess(videoId: string, mediaTitle: string, durationMs: number): void {
    appLogger.info(
      'Video initialization successful',
      `${this.SOURCE_PREFIX}.init`, { context: {
        videoId,
        mediaTitle,
        durationMs,
        timestamp: Date.now(),
      } }
    );
  }

  /**
   * Log video initialization error
   */
  public initError(videoId: string, mediaTitle: string, error: Error, context?: Record<string, any>): void {
    appLogger.error(
      'Video initialization failed',
      `${this.SOURCE_PREFIX}.init`, { context: {
        videoId,
        mediaTitle,
        errorMessage: error.message,
        ...context,
        timestamp: Date.now(),
      }, error: error }
    );
  }

  /**
   * Log HLS loader event
   */
  public hlsLoaderEvent(videoId: string, event: string, details?: Record<string, any>): void {
    appLogger.debug(
      `HLS Loader: ${event}`,
      `${this.SOURCE_PREFIX}.loader`, { context: {
        videoId,
        event,
        ...details,
        timestamp: Date.now(),
      } }
    );
  }

  /**
   * Log HLS error
   */
  public hlsError(videoId: string, errorType: string, errorDetails: any, fatal: boolean): void {
    const message = `HLS Error: ${errorType}`;
    const source = `${this.SOURCE_PREFIX}.hls`;
    const options = {
      context: {
        videoId,
        errorType,
        errorDetails,
        fatal,
        timestamp: Date.now(),
      },
    };

    if (fatal) {
      appLogger.error(message, source, options);
    } else {
      appLogger.warning(message, source, options);
    }
  }

  /**
   * Log decryption event
   */
  public decryptionEvent(videoId: string, success: boolean, segmentUrl?: string, error?: Error): void {
    if (!success) {
      appLogger.error(
        'Segment decryption failed',
        `${this.SOURCE_PREFIX}.decrypt`, { context: {
          videoId,
          segmentUrl,
          errorMessage: error?.message,
          timestamp: Date.now(),
        }, error: error }
      );

    }
  }

  /**
   * Log playback start
   */
  public playbackStart(videoId: string, mediaTitle: string): void {
    appLogger.info(
      'Video playback started',
      `${this.SOURCE_PREFIX}.playback`, { context: {
        videoId,
        mediaTitle,
        timestamp: Date.now(),
      } }
    );
  }

  /**
   * Log playback error
   */
  public playbackError(videoId: string, error: Error, context?: Record<string, any>): void {
    appLogger.error(
      'Video playback error',
      `${this.SOURCE_PREFIX}.playback`, { context: {
        videoId,
        errorMessage: error.message,
        ...context,
        timestamp: Date.now(),
      }, error: error }
    );
  }

  /**
   * Log fullscreen event
   */
  public fullscreenEvent(videoId: string, action: 'enter' | 'exit', success: boolean, error?: Error): void {
    if (success) {
      appLogger.debug(
        `Fullscreen ${action} successful`,
        `${this.SOURCE_PREFIX}.fullscreen`, { context: {
          videoId,
          action,
          timestamp: Date.now(),
        } }
      );
    } else {
      appLogger.warning(
        `Fullscreen ${action} failed`,
        `${this.SOURCE_PREFIX}.fullscreen`, { context: {
          videoId,
          action,
          errorMessage: error?.message,
          timestamp: Date.now(),
        } }
      );
    }
  }

  /**
   * Log general video warning
   */
  public warning(message: string, videoId: string, context?: Record<string, any>): void {
    appLogger.warning(
      message,
      `${this.SOURCE_PREFIX}.warning`, { context: {
        videoId,
        ...context,
        timestamp: Date.now(),
      } }
    );
  }
}

// Export singleton instance
export const videoLogger = new VideoLogger();
